import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session from Supabase
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user) {
        const userData = buildUserData(session?.user);
        setUser(userData);
        setUserProfile(userData);
      }
      setLoading(false);
    });

    // Listen for auth state changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((_event, session) => {
      if (session?.user) {
        const userData = buildUserData(session?.user);
        setUser(userData);
        setUserProfile(userData);
      } else {
        setUser(null);
        setUserProfile(null);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const buildUserData = (supabaseUser) => ({
    id: supabaseUser?.id,
    email: supabaseUser?.email,
    role: supabaseUser?.user_metadata?.role || 'admin',
    user_metadata: supabaseUser?.user_metadata || {},
  });

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({ email, password });
    if (error) {
      const err = new Error(error?.message || 'Invalid credentials');
      err.status = error?.status;
      err.code = error?.code;
      throw err;
    }

    const userData = buildUserData(data?.user);
    setUser(userData);
    setUserProfile(userData);

    return { data: { user: userData }, error: null };
  };

  const signUp = async (email, password, fullName, role = 'customer') => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: { data: { full_name: fullName, role } },
    });
    if (error) throw new Error(error?.message || 'Registration failed');
    return { data, error: null };
  };

  const signOut = async () => {
    await supabase?.auth?.signOut();
    setUser(null);
    setUserProfile(null);
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
